import { useId, useState } from "react";
import type { ImportDetail, ImportDraftSlot, ImportReport } from "../api/types";
import { ERROR_COPY, WARNING_COPY } from "../copy/errors";
import { Button, Card, Field, Input, Select } from "../ui";
import { SurfacePicker } from "./SurfacePicker";

export const TEXTURE_CHECK_HELP =
  "The numbered squares should look even and unstretched. If they are smeared or wildly different in size, press Fix stretching.";
export const BAKED_NOTE =
  "It may look wrong under the ad's lighting. You can still use it.";

/** What a surface that ads may change is made of: the class of surfaces it
 *  takes. */
const MADE_OF: { id: string; label: string }[] = [
  { id: "hard", label: "Hard material (metal, plastic, wood, stone)" },
  { id: "soft", label: "Fabric or leather" },
  { id: "glass", label: "Glass" },
];

/** A surface's typed name as the name it is stored under: lower case, every
 *  run of other characters one `_`, at most 24 characters; when nothing is
 *  left, the name the file gave it. */
export function renameOf(text: string, fallback: string): string {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 24) || fallback
  );
}

const ROW =
  "flex min-h-[44px] items-center gap-2 text-sm text-slate-800 sm:min-h-0";
const NOTE = "m-0 text-xs text-slate-600";

/** One surface of a model under review: its texture check, its name, and
 *  what ads may do with it. It edits the page's local copy; nothing is sent
 *  from here. */
export function ImportSlotCard(props: {
  /** The surface as the member has set it on this page. */
  slot: ImportDraftSlot;
  /** What the check found of it. */
  fact: ImportReport["slots"][number];
  /** Its name as the applied details have it: "Surface 1", "Seat". */
  label: string;
  checkUrl: string | null;
  materials: ImportDetail["materials"];
  /** The text of the Name field, as typed. */
  nameText: string;
  onNameText(text: string): void;
  onChange(changes: Partial<ImportDraftSlot>): void;
  /** The server refused a field of this surface. */
  invalid: boolean;
  onOpenPicture(src: string, alt: string): void;
}): JSX.Element {
  const { slot, fact, label } = props;
  const group = useId();
  const [picking, setPicking] = useState(false);
  const alt = `Texture check of ${label}`;
  const made = slot.accepts[0] ?? "hard";
  const chosen = props.materials.find((m) => m.id === slot.material);

  return (
    <Card className="flex flex-col gap-3 p-4 sm:p-5">
      <h3
        id={`${group}-label`}
        className="m-0 break-words text-base font-semibold text-slate-900"
      >
        {label}
      </h3>
      <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
        <div className="flex shrink-0 flex-col gap-2 sm:w-56">
          {props.checkUrl ? (
            <button
              type="button"
              className="block w-full cursor-zoom-in overflow-hidden rounded-lg border-0 bg-slate-100 p-0"
              onClick={() => props.onOpenPicture(props.checkUrl ?? "", alt)}
            >
              <img
                src={props.checkUrl}
                alt={alt}
                loading="lazy"
                className="block aspect-square w-full object-cover"
              />
            </button>
          ) : (
            <div
              role="img"
              aria-label={alt}
              className="flex aspect-square w-full items-center justify-center rounded-lg bg-slate-200 p-3 text-center text-xs text-slate-600"
            >
              {alt}
            </div>
          )}
          <p className={NOTE}>{TEXTURE_CHECK_HELP}</p>
          <div>
            <Button
              type="button"
              variant={slot.reunwrap ? "primary" : "secondary"}
              size="sm"
              className="min-h-[44px] sm:min-h-0"
              aria-pressed={slot.reunwrap}
              onClick={() => props.onChange({ reunwrap: !slot.reunwrap })}
            >
              Fix stretching
            </Button>
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <Field label="Name">
            <Input
              maxLength={40}
              className="min-h-[44px] sm:min-h-0"
              value={props.nameText}
              onChange={(event) => {
                props.onNameText(event.target.value);
                props.onChange({
                  rename: renameOf(event.target.value, slot.name),
                });
              }}
            />
          </Field>

          <div
            role="radiogroup"
            aria-labelledby={`${group}-label`}
            className="flex min-w-0 flex-col gap-1"
          >
            {fact.has_texture ? (
              <label className={ROW}>
                <input
                  type="radio"
                  name={group}
                  checked={slot.use === "native"}
                  onChange={() => props.onChange({ use: "native" })}
                />
                Keep its own texture
              </label>
            ) : null}
            <label className={ROW}>
              <input
                type="radio"
                name={group}
                checked={slot.use === "material"}
                onChange={() => props.onChange({ use: "material" })}
              />
              Let ads change its material
            </label>
            {slot.use === "material" ? (
              <div className="mb-2 ml-6 flex flex-col gap-3">
                <Field label="It is made of:">
                  <Select
                    className="min-h-[44px] sm:min-h-0"
                    value={made}
                    onChange={(event) =>
                      props.onChange({
                        accepts: [event.target.value],
                        // a default of another kind of material cannot stay
                        ...(chosen && chosen.surface !== event.target.value
                          ? { material: null }
                          : {}),
                      })
                    }
                  >
                    {MADE_OF.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="min-h-[44px] sm:min-h-0"
                    onClick={() => setPicking(true)}
                  >
                    Choose a surface
                  </Button>
                  {slot.material ? (
                    <span
                      className="min-w-0 break-words text-sm text-slate-800"
                      data-testid="default-surface"
                    >
                      {chosen?.name ?? "…"}
                    </span>
                  ) : null}
                </div>
                <div>
                  <Button
                    type="button"
                    variant={slot.material === null ? "primary" : "secondary"}
                    size="sm"
                    className="min-h-[44px] sm:min-h-0"
                    aria-pressed={slot.material === null}
                    onClick={() => props.onChange({ material: null })}
                  >
                    No default (keep the file's colour)
                  </Button>
                </div>
              </div>
            ) : null}
            {fact.coplanar ? (
              <label className={ROW}>
                <input
                  type="radio"
                  name={group}
                  checked={slot.use === "print"}
                  onChange={() => props.onChange({ use: "print" })}
                />
                A label or screen can go here
              </label>
            ) : null}
          </div>

          {fact.has_texture ? (
            <div className="flex flex-col gap-1">
              <label className={ROW}>
                <input
                  type="checkbox"
                  checked={slot.baked}
                  onChange={(event) =>
                    props.onChange({ baked: event.target.checked })
                  }
                />
                This texture has shadows or highlights painted in
              </label>
              {slot.baked ? <p className={NOTE}>{BAKED_NOTE}</p> : null}
            </div>
          ) : null}

          {fact.uv_overlap ? (
            <p className="m-0 text-sm text-slate-700">
              {WARNING_COPY.uv_overlap({})}
            </p>
          ) : null}
          {props.invalid ? (
            <p role="alert" className="tb-field-error m-0">
              {ERROR_COPY.validation_error}
            </p>
          ) : null}
        </div>
      </div>

      <SurfacePicker
        open={picking}
        onClose={() => setPicking(false)}
        accepts={[made]}
        onChoose={(item) => {
          props.onChange({ material: item.id });
          setPicking(false);
        }}
      />
    </Card>
  );
}
